import { Box, Typography, useTheme } from "@mui/material";
import { Constants } from "../../../Models/Constants";
import { IMultiSectionUnitConfiguration, IUnitConfiguration } from "../../../Models/IUnitConfiguration";
import { getFlowTypeUnits, getPreferredUnits } from "../../../Services/Global";
import { isRelevantOutputField } from "../../../Utils/UnitConfigurationHelper";
import { UnitAwareTextField } from "../../Atoms/UnitAwareTextField";

/**
 * Conditional text field outputs with Material UI
 * 
 * @param {boolean} disabled
 * @param {IUnitConfiguration | IMultiSectionUnitConfiguration} unitConfiguration
 * @param {any} handleChange
 * @param {(string | number)} hotMediaFlowrate
 * @param {(string | number)} hotMediaTempIn
 * @param {(string | number)} hotMediaTempOut
 * @param {(string | number)} hotMediaPressureDrop
 * @param {(string | number)} coldMediaFlowrate
 * @param {(string | number)} coldMediaTempIn
 * @param {(string | number)} coldMediaTempOut
 * @param {(string | number)} coldMediaPressureDrop
 * @param {string} hotMediaTempOutError
 * @param {string} hotMediaTempInError
 * @param {string} hotMediaFlowrateError
 * @param {string} coldMediaTempInError
 * @param {string} coldMediaFlowrateError
 * @param {string} coldMediaTempOutError
 * @param {string} coldMediaPressureDropError
 * @param {(string | number)} hotMediaWallTemp
 * @param {(string | number)} coldMediaWallTemp
 * @param {string} hotMediaWallTempError
 * @param {string} coldMediaWallTempError
 * @param {string} hotMediaPressureDropError
 * @param {boolean} addStyling
 * @return {*} 
 */
export const ConditionalTextFieldOutputs: React.FC<{
    disabled?: boolean,
    unitConfiguration: IUnitConfiguration | IMultiSectionUnitConfiguration,
    handleChange: any,
    hotMediaFlowrate?: string | number,
    hotMediaTempIn?: string | number,
    hotMediaTempOut?: string | number,
    hotMediaPressureDrop?: string | number,
    coldMediaFlowrate?: string | number,
    coldMediaTempIn?: string | number,
    coldMediaTempOut?: string | number,
    coldMediaPressureDrop?: string | number,
    hotMediaTempOutError?: string,
    hotMediaTempInError?: string,
    hotMediaFlowrateError?: string,
    coldMediaTempInError?: string,
    coldMediaFlowrateError?: string,
    coldMediaTempOutError?: string,
    coldMediaPressureDropError?: string,
    hotMediaWallTemp?: string | number,
    coldMediaWallTemp?: string | number,
    hotMediaWallTempError?: string,
    coldMediaWallTempError?: string,
    hotMediaPressureDropError?: string,
    addStyling?: boolean
}> = ({
    disabled = false,
    unitConfiguration,
    handleChange,
    hotMediaFlowrate,
    hotMediaTempIn,
    hotMediaTempOut,
    hotMediaPressureDrop,
    coldMediaFlowrate,
    coldMediaTempIn,
    coldMediaTempOut,
    coldMediaPressureDrop,
    hotMediaFlowrateError,
    hotMediaTempInError,
    hotMediaTempOutError,
    coldMediaTempInError,
    coldMediaFlowrateError,
    coldMediaTempOutError,
    coldMediaPressureDropError,
    hotMediaWallTemp,
    coldMediaWallTemp,
    hotMediaWallTempError,
    coldMediaWallTempError,
    hotMediaPressureDropError,
    addStyling
}) => {
    const theme = useTheme();
    
    const warmStyling = {
        border: `2px solid ${theme.palette.error.main}`,
        borderRadius: 1,
        padding: 2, 
        mb: 2
    };

    const coldStyling = {
        border: `2px solid ${theme.palette.primary.main}`,
        borderRadius: 1,
        padding: 2
    };

    const hotInputs = unitConfiguration?.outputs?.filter(x => 
        x === Constants.HotMediaFlowrate || 
        x === Constants.HotMediaTempIn || 
        x === Constants.HotMediaTempOut || 
        x === Constants.HotMediaPressureDrop || 
        x === Constants.HotMediaWallTemp
    );
    
    const shouldStyleHot = hotInputs && hotInputs.length > 0 ? true : false;
    
    const coldInputs = unitConfiguration?.outputs?.filter(x => 
        x === Constants.ColdMediaFlowrate || 
        x === Constants.ColdMediaTempIn || 
        x === Constants.ColdMediaTempOut || 
        x === Constants.ColdMediaPressureDrop || 
        x === Constants.ColdMediaWallTemp
    );
    
    const shouldStyleCold = coldInputs && coldInputs.length > 0 ? true : false;

    const preferredUnit = getPreferredUnits().preferredUnits;
    const temperatureUnit = preferredUnit === 'us' ? '°F' : '°C';
    const flowUnit = getFlowTypeUnits(unitConfiguration.flowType!);
    const pressureUnit = preferredUnit === 'us' ? 'PSI' : 'kPa';

    // Return empty fragment if no relevant fields
    if (
        !isRelevantOutputField(Constants.HotMediaFlowrate, unitConfiguration) &&
        !isRelevantOutputField(Constants.HotMediaTempIn, unitConfiguration) &&
        !isRelevantOutputField(Constants.HotMediaTempOut, unitConfiguration) &&
        !isRelevantOutputField(Constants.HotMediaPressureDrop, unitConfiguration) &&
        !isRelevantOutputField(Constants.ColdMediaFlowrate, unitConfiguration) &&
        !isRelevantOutputField(Constants.ColdMediaTempIn, unitConfiguration) &&
        !isRelevantOutputField(Constants.ColdMediaTempOut, unitConfiguration) &&
        !isRelevantOutputField(Constants.ColdMediaPressureDrop, unitConfiguration) &&
        !isRelevantOutputField(Constants.HotMediaWallTemp, unitConfiguration) &&
        !isRelevantOutputField(Constants.ColdMediaWallTemp, unitConfiguration)
    ) return (<></>);

    return (
        <>
            <Box sx={addStyling && shouldStyleHot ? warmStyling : {}} display="flex" flexDirection="column" gap={1}>
                {isRelevantOutputField(Constants.HotMediaFlowrate, unitConfiguration) && (
                    <Box>
                        <Typography variant="body2" gutterBottom>
                            {unitConfiguration.hotMediaType} flow rate
                        </Typography>
                        <UnitAwareTextField 
                            unitType={unitConfiguration?.flowType?.toLowerCase() === 'volumetric' ? 'volumetricFlow' : 'massFlow'}
                            type='number' 
                            disabled={disabled} 
                            inputProps={{ step: 0.1 }} 
                            onMetricValueChange={(val) => handleChange({ target: { name: Constants.HotMediaFlowrate, value: val } })} 
                            name={Constants.HotMediaFlowrate} 
                            metricValue={hotMediaFlowrate?.toString() || ''} 
                            InputProps={{ 
                                endAdornment: <Typography variant="caption" color="text.secondary">{flowUnit}</Typography> 
                            }}
                            helperText={hotMediaFlowrateError} 
                            error={!!hotMediaFlowrateError}
                            fullWidth
                            size="small"
                        />
                    </Box>
                )}

                {isRelevantOutputField(Constants.HotMediaTempIn, unitConfiguration) && (
                    <Box>
                        <Typography variant="body2" gutterBottom>
                            {unitConfiguration.hotMediaType} temperature inlet
                        </Typography>
                        <UnitAwareTextField 
                            unitType="temperature"
                            type='number' 
                            disabled={disabled} 
                            inputProps={{ step: 0.1 }} 
                            onMetricValueChange={(val) => handleChange({ target: { name: Constants.HotMediaTempIn, value: val } })} 
                            name={Constants.HotMediaTempIn} 
                            metricValue={hotMediaTempIn?.toString() || ''} 
                            InputProps={{ 
                                endAdornment: <Typography variant="caption" color="text.secondary">{temperatureUnit}</Typography> 
                            }}
                            helperText={hotMediaTempInError} 
                            error={!!hotMediaTempInError}
                            fullWidth
                            size="small"
                        />
                    </Box>
                )}

                {isRelevantOutputField(Constants.HotMediaTempOut, unitConfiguration) && (
                    <Box>
                        <Typography variant="body2" gutterBottom>
                            {unitConfiguration.hotMediaType} temperature outlet
                        </Typography>
                        <UnitAwareTextField 
                            unitType="temperature"
                            type='number' 
                            disabled={disabled} 
                            inputProps={{ step: 0.1 }} 
                            onMetricValueChange={(val) => handleChange({ target: { name: Constants.HotMediaTempOut, value: val } })} 
                            name={Constants.HotMediaTempOut} 
                            metricValue={hotMediaTempOut?.toString() || ''} 
                            InputProps={{ 
                                endAdornment: <Typography variant="caption" color="text.secondary">{temperatureUnit}</Typography> 
                            }}
                            helperText={hotMediaTempOutError} 
                            error={!!hotMediaTempOutError}
                            fullWidth
                            size="small"
                        />
                    </Box>
                )}

                {isRelevantOutputField(Constants.HotMediaPressureDrop, unitConfiguration) && (
                    <Box>
                        <Typography variant="body2" gutterBottom>
                            {unitConfiguration.hotMediaType} pressure drop
                        </Typography>
                        <UnitAwareTextField 
                            unitType="pressure"
                            type='number' 
                            disabled={disabled} 
                            inputProps={{ step: 0.1 }} 
                            onMetricValueChange={(val) => handleChange({ target: { name: Constants.HotMediaPressureDrop, value: val } })} 
                            name={Constants.HotMediaPressureDrop} 
                            metricValue={hotMediaPressureDrop?.toString() || ''} 
                            InputProps={{ 
                                endAdornment: <Typography variant="caption" color="text.secondary">{pressureUnit}</Typography> 
                            }}
                            helperText={hotMediaPressureDropError} 
                            error={!!hotMediaPressureDropError}
                            fullWidth
                            size="small"
                        />
                    </Box>
                )}

                {isRelevantOutputField(Constants.HotMediaWallTemp, unitConfiguration) && (
                    <Box>
                        <Typography variant="body2" gutterBottom>
                            {unitConfiguration.hotMediaType} wall temperature
                        </Typography>
                        <UnitAwareTextField 
                            unitType="temperature"
                            type='number' 
                            disabled={disabled} 
                            inputProps={{ step: 0.1 }} 
                            onMetricValueChange={(val) => handleChange({ target: { name: Constants.HotMediaWallTemp, value: val } })} 
                            name={Constants.HotMediaWallTemp} 
                            metricValue={hotMediaWallTemp?.toString() || ''} 
                            InputProps={{ 
                                endAdornment: <Typography variant="caption" color="text.secondary">{temperatureUnit}</Typography> 
                            }}
                            helperText={hotMediaWallTempError} 
                            error={!!hotMediaWallTempError}
                            fullWidth
                            size="small"
                        />
                    </Box>
                )}
            </Box>

            <Box sx={addStyling && shouldStyleCold ? coldStyling : {}} display="flex" flexDirection="column" gap={1}>
                {isRelevantOutputField(Constants.ColdMediaFlowrate, unitConfiguration) && (
                    <Box>
                        <Typography variant="body2" gutterBottom>
                            {unitConfiguration.coldMediaType} flow rate
                        </Typography>
                        <UnitAwareTextField 
                            unitType={unitConfiguration?.flowType?.toLowerCase() === 'volumetric' ? 'volumetricFlow' : 'massFlow'}
                            type='number' 
                            disabled={disabled} 
                            inputProps={{ step: 0.1 }} 
                            onMetricValueChange={(val) => handleChange({ target: { name: Constants.ColdMediaFlowrate, value: val } })} 
                            name={Constants.ColdMediaFlowrate} 
                            metricValue={coldMediaFlowrate?.toString() || ''} 
                            InputProps={{ 
                                endAdornment: <Typography variant="caption" color="text.secondary">{flowUnit}</Typography> 
                            }}
                            helperText={coldMediaFlowrateError} 
                            error={!!coldMediaFlowrateError}
                            fullWidth
                            size="small"
                        />
                    </Box>
                )}

                {isRelevantOutputField(Constants.ColdMediaTempIn, unitConfiguration) && (
                    <Box>
                        <Typography variant="body2" gutterBottom>
                            {unitConfiguration.coldMediaType} temperature inlet
                        </Typography>
                        <UnitAwareTextField 
                            unitType="temperature"
                            type='number' 
                            disabled={disabled} 
                            inputProps={{ step: 0.1 }} 
                            onMetricValueChange={(val) => handleChange({ target: { name: Constants.ColdMediaTempIn, value: val } })} 
                            name={Constants.ColdMediaTempIn} 
                            metricValue={coldMediaTempIn?.toString() || ''} 
                            InputProps={{ 
                                endAdornment: <Typography variant="caption" color="text.secondary">{temperatureUnit}</Typography> 
                            }}
                            helperText={coldMediaTempInError} 
                            error={!!coldMediaTempInError}
                            fullWidth
                            size="small"
                        />
                    </Box>
                )}

                {isRelevantOutputField(Constants.ColdMediaTempOut, unitConfiguration) && (
                    <Box>
                        <Typography variant="body2" gutterBottom>
                            {unitConfiguration.coldMediaType} temperature outlet
                        </Typography>
                        <UnitAwareTextField 
                            unitType="temperature"
                            type='number' 
                            disabled={disabled} 
                            inputProps={{ step: 0.1 }} 
                            onMetricValueChange={(val) => handleChange({ target: { name: Constants.ColdMediaTempOut, value: val } })} 
                            name={Constants.ColdMediaTempOut} 
                            metricValue={coldMediaTempOut?.toString() || ''} 
                            InputProps={{ 
                                endAdornment: <Typography variant="caption" color="text.secondary">{temperatureUnit}</Typography> 
                            }}
                            helperText={coldMediaTempOutError} 
                            error={!!coldMediaTempOutError}
                            fullWidth
                            size="small"
                        />
                    </Box>
                )}

                {isRelevantOutputField(Constants.ColdMediaPressureDrop, unitConfiguration) && (
                    <Box>
                        <Typography variant="body2" gutterBottom>
                            {unitConfiguration.coldMediaType} pressure drop
                        </Typography>
                        <UnitAwareTextField 
                            unitType="pressure"
                            type='number' 
                            disabled={disabled} 
                            inputProps={{ step: 0.1 }} 
                            onMetricValueChange={(val) => handleChange({ target: { name: Constants.ColdMediaPressureDrop, value: val } })} 
                            name={Constants.ColdMediaPressureDrop} 
                            metricValue={coldMediaPressureDrop?.toString() || ''} 
                            InputProps={{ 
                                endAdornment: <Typography variant="caption" color="text.secondary">{pressureUnit}</Typography> 
                            }}
                            helperText={coldMediaPressureDropError} 
                            error={!!coldMediaPressureDropError}
                            fullWidth
                            size="small"
                        />
                    </Box>
                )}

                {isRelevantOutputField(Constants.ColdMediaWallTemp, unitConfiguration) && (
                    <Box>
                        <Typography variant="body2" gutterBottom>
                            {unitConfiguration.coldMediaType} wall temperature
                        </Typography>
                        <UnitAwareTextField 
                            unitType="temperature"
                            type='number' 
                            disabled={disabled} 
                            inputProps={{ step: 0.1 }} 
                            onMetricValueChange={(val) => handleChange({ target: { name: Constants.ColdMediaWallTemp, value: val } })} 
                            name={Constants.ColdMediaWallTemp} 
                            metricValue={coldMediaWallTemp?.toString() || ''} 
                            InputProps={{ 
                                endAdornment: <Typography variant="caption" color="text.secondary">{temperatureUnit}</Typography> 
                            }}
                            helperText={coldMediaWallTempError} 
                            error={!!coldMediaWallTempError}
                            fullWidth
                            size="small"
                        />
                    </Box>
                )}
            </Box>
        </>
    );
}; 